import {
  SecondaryNavIdentifier,
  TertiaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { ResponsiveDesignProvider } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignProvider';
import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import MEDIA_QUERY_V2, { SCREEN_MASSIVE } from '@zola/zola-ui/src/styles/emotion/mediaQueryV2';

import styled from '@emotion/styled';
import cx from 'classnames';
import { Provider } from 'react-redux';

import ClientApp from '~/ClientApp';
import ClientRouter from '~/ClientRouter';
import MainLayout from '~/components/common/layouts/MainLayout/MainLayout';
import { UserContextProvider } from '~/contexts/UserContext';
import { SearchResultsPageProvider } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageProvider';
import { VendorSearchNavigationProvider } from '~/pages/couples/explore/components/SearchResults/contexts/VendorSearchNavigationProvider';
import { getStore } from '~/store';

import SearchResultsPageModalsAndToasts from '../../SearchResultsPageModalsAndToasts';
import { ExpertAdviceForSRP } from '../ExpertAdvice/ExpertAdviceForSrp';
import { PageSection } from '../PageSection/PageSection';
import { AdjacentVendors } from './components/AdjacentVendors/AdjacentVendors';
import { MobileSearchFilters } from './components/PreAuth/MobileSearchFilters';
import { PageHeading } from './components/PreAuth/PageHeading';
import { PreAuthSearchResults } from './components/PreAuth/PreAuthSearchResults';
import { RealWeddings } from './components/RealWeddings/RealWeddings';
import { SearchResultsPageBreadcrumbs } from './components/SearchBreadcrumb/SearchBreadcrumb';
import { SRPSearchBarProvider } from './components/SearchControls/SRPSearchBarContext';
import { SearchResultsHead } from './components/SearchResultsHead/SearchResultsHead';
import { SeoContentBlock } from './components/SeoContentBlock/SeoContentBlock';
import { VendorGridCardsProvider } from './contexts/VendorGridCardsProvider';
import { SearchResultsPageProps } from './types/props';

import styles from './searchResultsPageLayout.module.less';

const PageWrapper = styled.div`
  ${MEDIA_QUERY_V2.DESKTOP_MASSIVE} {
    display: flex;
    flex-direction: column;
    align-items: center;

    > div,
    > section {
      width: 100%;
      max-width: ${SCREEN_MASSIVE}px;
      border-left: 1px solid ${COLORS3.BLACK_030};
      border-right: 1px solid ${COLORS3.BLACK_030};
    }
  }
`;

const Divider = styled.div`
  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    display: none;
  }
  hr {
    padding: 0;
    margin: 0;
    margin-top: 24px;
    border-color: ${COLORS3.BLACK_030};
  }
`;

/**
 * Pre-auth (only to start) SRP A/B test.  Test variation changes:
 * - Grid of cards to list of cards
 * - Card presentation is wider, includes a review and a vendor description
 * - Location of favorite button is moved
 * - On desktop, a left rail of filter controls is used
 * - H1 header is changed
 */
export const TestPage = ({
  identifiedAsVendor,
  navHtml,
  parsedUrl,
  userAgent,
  userContext,
  ...rest
}: SearchResultsPageProps) => {
  useForceUpdateOnWindowResize();

  return (
    <UserContextProvider userContext={userContext}>
      <ResponsiveDesignProvider userAgent={userAgent}>
        <Provider store={getStore()}>
          <MainLayout
            activeSecondaryLink={SecondaryNavIdentifier.EXPLORE_VENDORS}
            activeTertiaryLink={
              parsedUrl.vendorSearch.selectedTaxonomy?.key as TertiaryNavIdentifier
            }
            ssrNavHtml={navHtml}
            className={cx(styles.mainLayout)}
            id="main"
          >
            <PageWrapper>
              <SearchResultsPageProvider
                {...rest}
                identifiedAsVendor={identifiedAsVendor}
                parsedUrl={parsedUrl}
              >
                <VendorSearchNavigationProvider>
                  <SearchResultsHead isInPreAuthSrpTest />
                  {/* 
                // No top picks in the pre-auth SRP
                <TopPicksSection
                  fromSrp
                  isPricingRatingTest1={isPricingRatingTest1}
                  isPricingRatingTest2={isPricingRatingTest2}
                /> */}
                  <SearchResultsPageBreadcrumbs location="top" />
                  <SRPSearchBarProvider>
                    <PageHeading />
                  </SRPSearchBarProvider>
                  <Divider>
                    <hr />
                  </Divider>

                  <MobileSearchFilters />
                  <VendorGridCardsProvider>
                    <PreAuthSearchResults />
                  </VendorGridCardsProvider>
                  <AdjacentVendors className={styles.withTopBorder} />
                  <ExpertAdviceForSRP className={styles.withTopBorder} />
                  <RealWeddings className={styles.withTopBorder} />
                  <PageSection className={styles.withTopBorder}>
                    <SeoContentBlock />
                  </PageSection>
                  <SearchResultsPageBreadcrumbs location="bottom" />
                </VendorSearchNavigationProvider>

                <ClientApp useStore={false}>
                  <ClientRouter>
                    <SearchResultsPageModalsAndToasts />
                  </ClientRouter>
                </ClientApp>
              </SearchResultsPageProvider>
            </PageWrapper>
          </MainLayout>
        </Provider>
      </ResponsiveDesignProvider>
    </UserContextProvider>
  );
};
